import "./style.css";
import { Game } from "./core/Game.js";
import { initTheme, setTheme, currentTheme } from "./ui/UITheme.js";
import { CHARACTERS } from "./config/characters.js";
import { achievementsFor, achieveProgress, earnedCount, achievementCount } from "./config/achievements.js";
import { completedCodexCount, totalCollected, CODEX_COUNT, TOTAL_MEMORIES } from "./config/memories.js";
import { initA11y, setReduceMotion, setColorblind, A11Y } from "./core/a11y.js";

initTheme();   // apply the saved / .env UI theme before anything renders
initA11y();    // apply saved accessibility prefs (reduced motion / colorblind palette)

// Version badge (top-left of the landing) — sourced from package.json via Vite.
(() => {
  const v = typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : null;
  const el = document.getElementById("versionBadge");
  if (el && v) el.textContent = "v" + v;
})();

// Desktop-only for now — warn touch/mobile/tablet visitors up front.
(() => {
  const coarse = window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
  const touch = "ontouchstart" in window || (navigator.maxTouchPoints || 0) > 0;
  const uaMobile = /Android|iPhone|iPad|iPod|IEMobile|Opera Mini|Mobile|Tablet|Silk|Kindle|PlayBook/i.test(navigator.userAgent || "");
  const small = Math.min(window.innerWidth, window.innerHeight) < 760;
  if ((coarse && touch) || uaMobile || small) {
    const m = document.getElementById("mobileNotice");
    if (m) {
      m.classList.remove("hidden");
      const c = document.getElementById("mnContinue");
      if (c) c.onclick = () => m.classList.add("hidden");
    }
  }
})();

const canvas = document.getElementById("game");
const game = new Game(canvas);
game.init();

// expose for debugging in the console — DEV builds only (stripped in production)
if (import.meta.env && import.meta.env.DEV) window.__game = game;

// flush any pending (debounced) save before the tab closes, and surface a notice
// if the browser refuses to persist (private mode / quota).
window.addEventListener("beforeunload", () => { try { game.save.flush(); } catch (_) {} });
game.save.onSaveError = () => { try { game.hud && game.hud.toast("⚠ Couldn't save — progress may not persist (private mode or storage full).", 6000); } catch (_) {} };

// When Settings is opened FROM the pause menu the pause overlay is hidden (so the
// two don't stack); fully leaving Settings (Done / ESC / backdrop) while the game
// is still paused brings the pause menu back. Keyed off game.state so there's no
// flag to go stale — opening Settings from the landing (state "menu") won't
// summon a pause menu.
function restorePauseAfterSettings() {
  if (game.state === "paused") document.getElementById("pauseScreen")?.classList.remove("hidden");
}

// ---- Universal modal dismissal ----
// Every dismissable overlay closes the same three ways: ESC, clicking the
// backdrop outside the box, and (for key-opened panels) re-pressing the opener.
// These ids are the light/utility modals; blocking decisions (revive, save
// update) and the pause screen are intentionally excluded.
(() => {
  const DISMISSABLE = ["settingsModal", "creditsModal", "achievementsModal", "featsModal", "statsModal", "chestModal"];
  const topOpen = () => {
    for (let i = DISMISSABLE.length - 1; i >= 0; i--) {
      const el = document.getElementById(DISMISSABLE[i]);
      if (el && !el.classList.contains("hidden")) return el;
    }
    return null;
  };
  // ESC — capture phase so it runs before Game's ESC→pause and can swallow it.
  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    const el = topOpen();
    if (el) { e.stopPropagation(); e.preventDefault(); el.classList.add("hidden"); if (el.id === "settingsModal") restorePauseAfterSettings(); }
  }, true);
  // Backdrop click closes the modal (clicks on the inner box don't bubble to here).
  DISMISSABLE.forEach((id) => {
    const el = document.getElementById(id);
    if (el) el.addEventListener("mousedown", (e) => { if (e.target === el) { el.classList.add("hidden"); if (id === "settingsModal") restorePauseAfterSettings(); } });
  });
  // Waystation station panels: click the backdrop outside the panel to close.
  const inter = document.getElementById("intermissionScreen");
  if (inter) inter.addEventListener("mousedown", (e) => {
    if (e.target === inter && game.hubPanelOpen) game.closeStation();
  });
})();

// UI theme switcher on the landing screen (persists the choice)
const uiEl = document.getElementById("uiTheme");
if (uiEl) {
  const mark = () => uiEl.querySelectorAll("[data-theme]").forEach((b) => b.classList.toggle("active", b.dataset.theme === currentTheme()));
  uiEl.querySelectorAll("[data-theme]").forEach((b) => (b.onclick = () => { setTheme(b.dataset.theme); mark(); }));
  mark();
}

// "Continue Run" on the landing, only when a resumable run is saved
const continueBtn = document.getElementById("continueBtn");
if (continueBtn && game.save && game.save.hasRun() && !(game.save.incompatible)) {
  continueBtn.style.display = "";
  continueBtn.onclick = () => game.continueRun();
}

// breaking-update prompt: existing save is from an incompatible schema version
if (game.save && game.save.incompatible) {
  const modal = document.getElementById("saveUpdateModal");
  if (modal) {
    modal.classList.remove("hidden");
    const reset = document.getElementById("suReset");
    const later = document.getElementById("suLater");
    if (reset) reset.onclick = () => { game.save.resetForUpdate(); location.reload(); };
    if (later) later.onclick = () => modal.classList.add("hidden");
  }
}

// ---- Settings modal (audio, banana jump scare, clear save) ----
(() => {
  const modal = document.getElementById("settingsModal");
  if (!modal) return;
  const $ = (id) => document.getElementById(id);
  const music = $("setMusic"), musicVal = $("setMusicVal");
  const sfx = $("setSfx"), sfxVal = $("setSfxVal");
  const mute = $("setMute"), banana = $("setBanana");
  const reduceMotion = $("setReduceMotion"), colorblind = $("setColorblind");
  const clear = $("setClear"), clearNote = $("setClearNote");
  let clearArmed = false, clearTimer = null;

  const setSwitch = (btn, on) => { btn.textContent = on ? "On" : "Off"; btn.setAttribute("aria-checked", on ? "true" : "false"); btn.classList.toggle("on", on); };

  const sync = () => {
    const a = game.audio;
    if (a) {
      music.value = Math.round((a.musicVol ?? 0.6) * 100); musicVal.textContent = music.value + "%";
      sfx.value = Math.round((a.sfxVolume ?? 0.7) * 100); sfxVal.textContent = sfx.value + "%";
      setSwitch(mute, !!a.muted);
    }
    setSwitch(banana, game.bananaScare ? game.bananaScare.isEnabled() : true);
    if (reduceMotion) setSwitch(reduceMotion, A11Y.reduceMotion);
    if (colorblind) setSwitch(colorblind, A11Y.colorblind);
    // reset the New Legacy confirmation each open
    clearArmed = false; clear.textContent = "Start a New Legacy — erase all progress…"; clear.classList.remove("armed");
    clearNote.textContent = "Normal new runs keep your progress (the Helix, storage, research, hero levels). This is the only full reset: it wipes everything for a completely fresh start.";
  };
  const open = () => { sync(); modal.classList.remove("hidden"); };
  const close = () => { modal.classList.add("hidden"); };                 // plain hide (used by sub-screen nav)
  const finish = () => { close(); restorePauseAfterSettings(); };         // fully leave Settings

  $("settingsBtn") && ($("settingsBtn").onclick = open);
  // From the pause menu: hide the pause overlay first so they never stack.
  $("pauseSettingsBtn") && ($("pauseSettingsBtn").onclick = () => { document.getElementById("pauseScreen")?.classList.add("hidden"); open(); });
  $("setClose") && ($("setClose").onclick = finish);

  // Credits screen (opened from Settings)
  const credits = document.getElementById("creditsModal");
  $("setCredits") && ($("setCredits").onclick = () => { close(); credits && credits.classList.remove("hidden"); });
  $("creditsClose") && ($("creditsClose").onclick = () => credits && credits.classList.add("hidden"));

  // Achievements screen (per hero) — opened from Settings
  const ach = document.getElementById("achievementsModal");
  const renderAch = () => {
    const box = document.getElementById("achContent"); if (!box) return;
    box.innerHTML = CHARACTERS.map((ch) => {
      const prof = game.save.profile(ch.id);
      const total = achievementCount(ch.id);
      const earned = earnedCount(prof, ch.id), mastered = earned >= total;
      const rows = achievementsFor(ch.id).map((a) => {
        const pr = achieveProgress(prof, a);
        const tag = a.hero ? `<span class="ach-tag">HERO</span>` : "";
        return `<div class="ach-row${pr.done ? " done" : ""}">
          <span class="ach-ic">${pr.done ? "★" : "☆"}</span>
          <span class="ach-t"><b>${a.title}${tag}</b><span>${a.desc}</span></span>
          <span class="ach-p">${Math.min(pr.have, pr.need)}/${pr.need}</span></div>`;
      }).join("");
      return `<div class="ach-hero">
        <div class="ach-hero-head"><b style="color:${ch.color}">${ch.glyph || ""} ${ch.name}</b>
          <span>${earned}/${total}${mastered ? " · ✦ MASTERED" : ""}</span></div>
        ${rows}</div>`;
    }).join("");
  };
  $("setAchievements") && ($("setAchievements").onclick = () => { close(); renderAch(); ach && ach.classList.remove("hidden"); });
  $("achClose") && ($("achClose").onclick = () => ach && ach.classList.add("hidden"));

  // Statistics / collection screen — opened from Settings
  const stats = document.getElementById("statsModal");
  const notorietyLabel = (n) => n > 800 ? "The Vault knows your name" : n > 400 ? "The Vault remembers you" : n > 150 ? "The Vault watches you" : n > 50 ? "The Vault has noticed you" : "Unremembered";
  const renderStats = () => {
    const box = document.getElementById("statsContent"); if (!box) return;
    const meta = game.meta || game.save.meta();
    const codex = meta.codex || {};
    const codexDone = completedCodexCount(codex), memColl = totalCollected(codex);
    const pct = (a, b) => b ? Math.round((a / b) * 100) : 0;
    const forged = meta.helix && meta.helix.forged ? Object.keys(meta.helix.forged).filter((k) => meta.helix.forged[k]).length : 0;
    const research = meta.research ? Object.keys(meta.research).filter((k) => meta.research[k]).length : 0;
    const pets = meta.pets ? Object.keys(meta.pets).filter((k) => meta.pets[k] && meta.pets[k].unlocked).length : 0;
    const overview = [
      ["Helix restored", `${codexDone}/${CODEX_COUNT} · ${pct(codexDone, CODEX_COUNT)}%`],
      ["Memories recovered", `${memColl}/${TOTAL_MEMORIES} · ${pct(memColl, TOTAL_MEMORIES)}%`],
      ["Legendary relics forged", `${forged}`],
      ["Deepest reached", `floor ${meta.deepest || 0}`],
      ["Research unlocked", `${research}`],
      ["Companions tamed", `${pets}`],
      ["Insight", `${Math.floor(meta.insight || 0)}`],
      ["Copper banked", `${Math.floor(meta.copper || 0)}`],
      ["Bag slots", `${20 + (meta.bagExpansions || 0)}`],
      ["Notoriety", notorietyLabel(meta.notoriety || 0)],
    ];
    const overHTML = `<div class="stats-grid">${overview.map(([k, v]) => `<div class="stat-cell"><span class="stat-k">${k}</span><span class="stat-v">${v}</span></div>`).join("")}</div>`;
    const heroHTML = CHARACTERS.map((ch) => {
      const prof = game.save.profile(ch.id);
      const earned = earnedCount(prof, ch.id), total = achievementCount(ch.id);
      return `<div class="stats-hero"><b style="color:${ch.color}">${ch.glyph || ""} ${prof.name || ch.name}</b>
        <span>Lv ${prof.level || 1} · ${Math.floor(prof.kills || 0)} kills · depth ${prof.best || 0} · ${prof.bosses || 0} bosses · ${prof.runs || 0} runs · ${earned}/${total} feats${earned >= total ? " ✦" : ""}</span></div>`;
    }).join("");
    box.innerHTML = `<div class="stats-sec-label">The Vault</div>${overHTML}<div class="stats-sec-label">Heroes</div>${heroHTML}`;
  };
  $("setStats") && ($("setStats").onclick = () => { close(); renderStats(); stats && stats.classList.remove("hidden"); });
  $("statsClose") && ($("statsClose").onclick = () => stats && stats.classList.add("hidden"));

  music.oninput = () => { musicVal.textContent = music.value + "%"; game.audio?.setMusicVolume(music.value / 100); };
  sfx.oninput = () => { sfxVal.textContent = sfx.value + "%"; game.audio?.setSfxVolume(sfx.value / 100); };
  sfx.onchange = () => game.audio?.previewSfx();
  mute.onclick = () => { const on = !(game.audio?.muted); game.audio?.setMuted(on); setSwitch(mute, on); };
  banana.onclick = () => { const on = game.bananaScare ? game.bananaScare.toggle() : true; setSwitch(banana, on); };
  if (reduceMotion) reduceMotion.onclick = () => { const on = !A11Y.reduceMotion; setReduceMotion(on); setSwitch(reduceMotion, on); };
  if (colorblind) colorblind.onclick = () => { const on = !A11Y.colorblind; setColorblind(on); setSwitch(colorblind, on); if (game.hud) { game.hud._sig = ""; if (game.hud.inventoryOpen) game.hud.renderInventory(); } };

  clear.onclick = () => {
    if (!clearArmed) {
      clearArmed = true;
      clear.textContent = "Click again to erase EVERYTHING & start fresh";
      clear.classList.add("armed");
      clearNote.textContent = "This erases ALL progress and cannot be undone. Click once more to begin a new legacy.";
      clearTimer = setTimeout(() => { clearArmed = false; clear.textContent = "Start a New Legacy — erase all progress…"; clear.classList.remove("armed"); }, 4000);
      return;
    }
    clearTimeout(clearTimer);
    try { game.save.clear(); } catch (_) {}
    location.reload();
  };
})();

// dev-only debug menu (bottom-right), shown only on localhost
if (["localhost", "127.0.0.1", ""].includes(location.hostname)) {
  import("./ui/DebugMenu.js").then((m) => new m.DebugMenu(game)).catch(() => {});
}
